// Performance utilities to prevent forced synchronous layout
class LayoutOptimizer {
  constructor() {
    this.rafCallbacks = new Set();
    this.isScheduled = false;
  }

  // Schedule layout reads/writes for next animation frame
  scheduleLayoutWork(callback) {
    this.rafCallbacks.add(callback);
    
    if (!this.isScheduled) {
      this.isScheduled = true;
      requestAnimationFrame(() => {
        // Execute all scheduled work in a single frame
        this.rafCallbacks.forEach(cb => {
          try {
            cb();
          } catch (error) {
            console.warn('Layout work failed:', error);
          }
        });
        
        this.rafCallbacks.clear();
        this.isScheduled = false;
      });
    }
  }

  // Batch DOM measurements to prevent layout thrashing
  batchMeasurements(elements, callback) {
    const measurements = [];
    
    // Phase 1: Read all measurements (causes single layout)
    this.scheduleLayoutWork(() => {
      elements.forEach((element, index) => {
        if (element && element.getBoundingClientRect) {
          measurements[index] = {
            rect: element.getBoundingClientRect(),
            scrollLeft: element.scrollLeft || 0,
            scrollTop: element.scrollTop || 0,
            offsetWidth: element.offsetWidth || 0,
            offsetHeight: element.offsetHeight || 0
          };
        }
      });
      
      // Phase 2: Execute callback with measurements
      if (callback) {
        callback(measurements);
      }
    });
  }

  // Optimized scroll handling
  optimizedScrollHandler(element, onScroll, throttleMs = 16) {
    let ticking = false;
    let lastScrollLeft = 0;
    let lastScrollTop = 0;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollLeft = element.scrollLeft;
          const currentScrollTop = element.scrollTop;
          
          // Only fire callback if scroll actually changed
          if (currentScrollLeft !== lastScrollLeft || currentScrollTop !== lastScrollTop) {
            onScroll({
              scrollLeft: currentScrollLeft,
              scrollTop: currentScrollTop,
              deltaX: currentScrollLeft - lastScrollLeft,
              deltaY: currentScrollTop - lastScrollTop
            });
            
            lastScrollLeft = currentScrollLeft;
            lastScrollTop = currentScrollTop;
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };
    
    return handleScroll;
  }

  // Optimized smooth scrolling without forced layout
  smoothScrollTo(element, targetScrollLeft, duration = 300) {
    if (!element) return Promise.resolve();
    
    return new Promise((resolve) => {
      const startScrollLeft = element.scrollLeft;
      const distance = targetScrollLeft - startScrollLeft;
      const startTime = performance.now();
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeInOutCubic = progress < 0.5 
          ? 4 * progress * progress * progress
          : (progress - 1) * (2 * progress - 2) * (2 * progress - 2) + 1;
        
        element.scrollLeft = startScrollLeft + (distance * easeInOutCubic);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animateScroll);
    });
  }

  // Optimize DOM updates to prevent layout thrashing
  batchDOMUpdates(updates) {
    // Batch all DOM reads first
    const measurements = [];
    updates.forEach((update, index) => {
      if (update.read && update.element) {
        measurements[index] = update.read(update.element);
      }
    });
    
    // Then batch all DOM writes
    this.scheduleLayoutWork(() => {
      updates.forEach((update, index) => {
        if (update.write && update.element) {
          update.write(update.element, measurements[index]);
        }
      });
    });
  }
}

// Create singleton instance
const layoutOptimizer = new LayoutOptimizer();

// Intersection Observer utility for performance
export const createOptimizedObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: [0, 0.1, 0.5, 1.0]
  };
  
  const mergedOptions = { ...defaultOptions, ...options };
  
  return new IntersectionObserver((entries) => {
    // Batch all intersection updates
    layoutOptimizer.scheduleLayoutWork(() => {
      callback(entries);
    });
  }, mergedOptions);
};

// Performance-optimized resize observer
export const createOptimizedResizeObserver = (callback) => {
  if (!window.ResizeObserver) {
    // Fallback for older browsers
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(callback, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return { disconnect: () => window.removeEventListener('resize', handleResize) };
  }
  
  return new ResizeObserver((entries) => {
    layoutOptimizer.scheduleLayoutWork(() => {
      callback(entries);
    });
  });
};

// Export utilities
export default layoutOptimizer;
